import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoHead from "../SiteSeo";
import SiteLayout from "../SiteLayout";
import { CalendlyLink, SmartLink } from "../SiteHeader";
import { FinalCta } from "../sections";
import { toolBySlug, TOOLS, buildPresentation } from "./tools";
import { getPartnerSlug, captureRefFromUrl } from "../../pages/public/partnerRef";
import { SharedByStrip } from "../SharedByStrip";
import ComingSoonPage from "../ComingSoonPage";

const API = process.env.REACT_APP_BACKEND_URL;

const money0 = (v) => `$${Math.round(Number(v) || 0).toLocaleString("en-CA")}`;

function Field({ f, value, onChange, inputs, results }) {
  const id = `calc-${f.key}`;
  const label = typeof f.label === "function" ? f.label(inputs) : f.label;
  if (f.type === "segment") {
    return (
      <div className="calc-field" role="radiogroup" aria-label={label}><span>{label}</span>
        <div className="calc-segment">{f.options.map(([v, l, sub]) => <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? "active" : ""} onClick={() => onChange(f.key, v)} data-testid={`calc-input-${f.key}-${v}`}><strong>{l}</strong>{sub && <small>{sub}</small>}</button>)}</div>
      </div>
    );
  }
  if (f.type === "range") {
    const max = Math.max(Number(results?.[f.maxFrom]) || 0, 0);
    const shown = value === "" || value == null ? Number(results?.[f.valueFrom]) || 0 : Math.min(Number(value), max);
    return (
      <label className="calc-field" htmlFor={id}><span>{label}<b className="calc-range-value" data-testid={`calc-range-value-${f.key}`}>{money0(shown)}</b></span>
        <input id={id} type="range" min="0" max={max || 0} step={f.step || 1000} value={shown} disabled={!max} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`calc-input-${f.key}`} />
        <small className="calc-hint">{max ? `Up to ${money0(max)} available (80% of your home’s value, minus what you owe). We start you at the sweet spot — pull more and your income limit drops; pull less and your down payment does.` : "No equity available to pull at 80% of value."}</small>
      </label>
    );
  }
  if (f.type === "select") {
    return (
      <label className="calc-field" htmlFor={id}><span>{label}</span>
        <select id={id} value={value} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`calc-input-${f.key}`}>{f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </label>
    );
  }
  if (f.type === "bool") {
    return <label className="calc-field calc-check" htmlFor={id}><input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(f.key, e.target.checked)} data-testid={`calc-input-${f.key}`} /><span>{label}</span></label>;
  }
  if (f.type === "computed") {
    const v = f.compute(inputs), hint = f.hint ? f.hint(inputs) : "";
    return (
      <div className="calc-field calc-computed" data-testid={`calc-computed-${f.key}`}><span>{label}</span>
        <div className="calc-input money readonly" aria-live="polite"><em>$</em><output data-testid={`calc-input-${f.key}`}>{Math.round(v).toLocaleString("en-CA")}</output></div>
        {hint && <small className="calc-hint">{hint}</small>}
      </div>
    );
  }
  return (
    <label className="calc-field" htmlFor={id}><span>{label}</span>
      <div className={`calc-input ${f.type}`}>
        {f.type === "money" && <em>$</em>}
        <input id={id} type="number" inputMode="decimal" step={f.type === "pct" ? "0.01" : "1"} value={value} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`calc-input-${f.key}`} />
        {f.type === "pct" && <em>%</em>}{f.unit ? <em>{f.unit}</em> : f.type === "years" || f.type === "int" ? <em>yrs</em> : null}
      </div>
    </label>
  );
}

export function LeadCapture({ tool, inputs, results }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault(); setState("sending");
    try {
      const r = await fetch(`${API}/api/public/calc/lead/save`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool: tool.slug, ...form, inputs, results, presentation: buildPresentation(tool, inputs, results), source_partner_slug: getPartnerSlug() || null, page_path: window.location.pathname }) });
      setState(r.ok ? "done" : "error");
    } catch { setState("error"); }
  };
  if (state === "done") return <div className="calc-lead calc-lead-done" data-testid="calc-lead-success"><strong>Sent. Check your inbox.</strong><p>Your PDF breakdown is on its way. When you want a human to look at the whole picture, we’re one click away.</p><CalendlyLink className="btn light" testId="calc-lead-calendly">Talk it through</CalendlyLink></div>;
  return (
    <form className="calc-lead" onSubmit={submit} data-testid="calc-lead-form">
      <strong>Want this as a PDF?</strong>
      <p>We’ll email you a designed one-pager with these numbers, so you can come back to them or forward it on. No pressure, no mortgage-speak.</p>
      <div className="calc-lead-row">
        <input required placeholder="First name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="First name" autoComplete="given-name" data-testid="calc-lead-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="Email" data-testid="calc-lead-email" />
        <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} aria-label="Phone" data-testid="calc-lead-phone" />
      </div>
      <div className="calc-lead-actions">
        <button className="btn" type="submit" disabled={state === "sending"} data-testid="calc-lead-submit">{state === "sending" ? "Sending…" : "Email me the PDF"}</button>
        <CalendlyLink className="textlink light" testId="calc-talk">Talk it through instead</CalendlyLink>
      </div>
      {state === "error" && <div className="calc-error" role="alert" data-testid="calc-lead-error">That didn’t send. Try again or email {`sayhello@hellomortgage.ca`}.</div>}
      <small>By sending, you agree we can email you this breakdown and occasional mortgage insights. Unsubscribe anytime.</small>
    </form>
  );
}

export default function CalculatorPage() {
  const { slug } = useParams();
  const tool = toolBySlug(slug);
  const [search] = useSearchParams();
  // Prefill from the link (QR on a Suite Advantage page, realtor share): ?price=…&monthly_rent=…&from=<address label>
  const prefill = useMemo(() => {
    const out = {};
    (tool?.fields || []).forEach((f) => {
      const raw = search.get(f.key);
      if (raw == null || raw === "") return;
      if (f.type === "money" || f.type === "pct" || f.type === "years" || f.type === "int") { const n = Number(raw); if (Number.isFinite(n)) out[f.key] = n; }
      else if (f.type === "select") { const hit = f.options.find(([v]) => String(v) === raw); if (hit) out[f.key] = hit[0]; }
      else if (f.type === "segment") { if (f.options.some(([v]) => v === raw)) out[f.key] = raw; }
      else if (f.type === "bool") out[f.key] = raw === "1" || raw === "true";
    });
    return out;
  }, [tool, search]);
  const prefillFrom = search.get("from");
  useEffect(() => { captureRefFromUrl(); }, []);
  const [inputs, setInputs] = useState(() => ({ ...Object.fromEntries((tool?.fields || []).map((f) => [f.key, f.default])), ...prefill }));
  const [defaults, setDefaults] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showAdv, setShowAdv] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/public/calc/defaults`).then((r) => r.json()).then((d) => {
      setDefaults(d);
      setInputs((prev) => { const n = { ...prev }; (tool?.fields || []).forEach((f) => { if (f.rateDefault && d[f.rateDefault] && prefill[f.key] == null) n[f.key] = d[f.rateDefault]; }); return n; });
    }).catch(() => setDefaults({}));
  }, [tool, prefill]);

  useEffect(() => {
    if (!tool) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const body = Object.fromEntries(Object.entries(inputs).filter(([, v]) => v !== ""));
        tool.fields.filter((f) => f.type === "computed").forEach((f) => { body[f.key] = f.compute(inputs); });
        const r = await fetch(`${API}/api/public/calc/${tool.slug}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        const data = await r.json();
        if (!r.ok) { setError(data.detail || "Check your inputs."); return; }
        setError(null); setResults(data);
      } catch { setError("We couldn’t run the numbers just now."); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [inputs, tool]);

  const related = useMemo(() => TOOLS.filter((t) => t.slug !== slug && t.category === tool?.category).slice(0, 3), [slug, tool]);
  if (!tool) return <ComingSoonPage />;
  const onChange = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const applyPreset = (p) => setInputs((prev) => ({ ...prev, ...p.values, ...(tool.fields.some((f) => f.type === "range") ? { equity_to_pull: "" } : {}) }));
  const visible = (f) => !f.show || f.show(inputs);
  const renderField = (f) => (
    <React.Fragment key={f.key}>
      {f.group && <div className="calc-group-title" data-testid={`calc-group-${f.key}`}>{f.group}</div>}
      <Field f={f} value={inputs[f.key]} onChange={onChange} inputs={inputs} results={results} />
    </React.Fragment>
  );
  const head = results ? tool.headline(results) : null;
  const liveRate = tool.fields.find((f) => f.rateDefault) && defaults && defaults[tool.fields.find((f) => f.rateDefault).rateDefault];
  const ld = { "@context": "https://schema.org", "@type": "WebApplication", name: `${tool.name} Calculator`, applicationCategory: "FinanceApplication", operatingSystem: "Web", url: `https://www.hellomortgage.ca/calculators/${tool.slug}/`, offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" }, provider: { "@type": "Organization", name: "Hello Mortgage Team" } };

  return (
    <SiteLayout>
      <SeoHead title={`${tool.name} Calculator | Hello Mortgage Team`} description={tool.blurb} path={`/calculators/${tool.slug}/`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <SharedByStrip />
      <section className="calc-hero" data-testid={`calculator-${tool.slug}`}>
        <div className="wrap">
          <SmartLink className="textlink" href="/calculators/">← All calculators</SmartLink>
          <div className="eyebrow" style={{ marginTop: 26 }}>{tool.name} calculator</div>
          <h1>{tool.tagline}</h1>
          <p>{tool.blurb}{liveRate ? ` Pre-filled with today’s best 5-year fixed from our lender feed (${liveRate}%) — change anything.` : " Enter your own rate — every field is editable."}</p>
        </div>
      </section>
      <section className="calc-body">
        <div className="wrap calc-grid">
          <form className="calc-form" onSubmit={(e) => e.preventDefault()} data-testid="calc-form">
            {prefillFrom && Object.keys(prefill).length > 0 && (
              <div className="calc-presets" data-testid="calc-prefill-note"><span>Numbers from <strong>{prefillFrom}</strong> — adjust anything.</span></div>
            )}
            {tool.presets && (
              <div className="calc-presets" data-testid="calc-presets">
                <span>Not sure? Try a real scenario</span>
                <div>{tool.presets.map((p) => <button key={p.name} type="button" onClick={() => applyPreset(p)} data-testid={`calc-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}><strong>{p.name}</strong><small>{p.sub}</small></button>)}</div>
              </div>
            )}
            {tool.fields.filter((f) => !f.advanced && visible(f)).map(renderField)}
            {tool.fields.some((f) => f.advanced && visible(f)) && (
              <>
                <button type="button" className="calc-adv-toggle" onClick={() => setShowAdv(!showAdv)} aria-expanded={showAdv} data-testid="calc-advanced-toggle">{showAdv ? "Hide" : "Show"} the extra details<span className="plus" aria-hidden="true">+</span></button>
                {showAdv && tool.fields.filter((f) => f.advanced && visible(f)).map(renderField)}
              </>
            )}
            {error && <div className="calc-error" role="alert" data-testid="calc-error">{error}</div>}
          </form>
          <aside className="calc-results" aria-live="polite" data-testid="calc-results">
            {head && (<>
              {tool.banner && (() => { const b = tool.banner(results, inputs); return b && <div className={`calc-banner ${b.tone}`} data-testid="calc-verdict-banner"><strong data-testid="calc-verdict-title">{b.title}</strong><p>{b.text}</p></div>; })()}
              {tool.kpis && <div className="calc-kpis" data-testid="calc-kpis">{tool.kpis(results, inputs).map(([l, v]) => <div key={l}><span>{l}</span><strong>{v}</strong></div>)}</div>}
              <div className="calc-headline"><span>{head.label}</span><strong data-testid="calc-headline-value">{head.value}</strong></div>
              {tool.verdict && <p className="calc-verdict" data-testid="calc-verdict">{tool.verdict(results, inputs)}</p>}
              {tool.warning && tool.warning(results, inputs) && <div className="calc-warning" role="status" data-testid="calc-warning">{tool.warning(results, inputs)}</div>}
              <dl className="calc-summary">{tool.summary(results, inputs).map(([l, v]) => <div key={l}><dt>{l}</dt><dd>{v}</dd></div>)}</dl>
              {tool.items && <div className="calc-items"><h3>Line by line</h3>{tool.items(results).map(([l, v]) => <div className="calc-item" key={l}><span>{l}</span><strong>{v}</strong></div>)}</div>}
              {tool.compare && (() => { const c = tool.compare(results, inputs); return (
                <div className="calc-compare" data-testid="calc-compare">
                  <table>
                    <thead><tr><th /><th>{c.cols[0]}</th><th>{c.cols[1]}</th></tr></thead>
                    {c.groups.map((g) => (
                      <tbody key={g.title}>
                        <tr className="calc-compare-group"><th colSpan={3}>{g.title}</th></tr>
                        {g.rows.map(([l, a, b]) => <tr key={l}><td>{l}</td><td>{a}</td><td>{b}</td></tr>)}
                      </tbody>
                    ))}
                  </table>
                </div>); })()}
              {tool.tables && tool.tables(results, inputs).map((t) => <div className="calc-items" key={t.title} data-testid={`calc-table-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}><h3>{t.title}</h3>{t.rows.map(([l, v]) => <div className="calc-item" key={l}><span>{l}</span><strong>{v}</strong></div>)}</div>)}
              {tool.schedule && results.years?.length > 0 && (
                <details className="calc-schedule"><summary>Year-by-year breakdown</summary>
                  <table><thead><tr><th>Year</th><th>Interest</th><th>Principal</th><th>Balance</th>{tool.scheduleExtra && <th>{tool.scheduleExtra[0]}</th>}</tr></thead>
                    <tbody>{tool.schedule(results).map((y) => <tr key={y.year}><td>{y.year}</td><td>${Math.round(y.interest).toLocaleString("en-CA")}</td><td>${Math.round(y.principal).toLocaleString("en-CA")}</td><td>${Math.round(y.balance).toLocaleString("en-CA")}</td>{tool.scheduleExtra && <td>${Math.round(y[tool.scheduleExtra[1]]).toLocaleString("en-CA")}</td>}</tr>)}</tbody></table>
                </details>
              )}
              <LeadCapture tool={tool} inputs={inputs} results={results} />
            </>)}
            {!head && !error && <div className="calc-headline"><span>Running the numbers…</span></div>}
          </aside>
        </div>
        <div className="wrap calc-fine"><p><strong>The fine print, honestly.</strong> {tool.fine ? `${tool.fine} ` : ""}These are estimates using standard Canadian lending rules (semi-annual compounding, CMHC premium tiers, GDS 39% / TDS 44%, the OSFI stress test). Lender policies, your credit profile and property details can change the picture — that’s the part a conversation covers.</p></div>
      </section>
      {related.length > 0 && (
        <section className="calc-related"><div className="wrap">
          <div className="eyebrow">Keep going</div><h2 className="display">More math for the same move.</h2>
          <div className="tool-grid">{related.map((t) => <SmartLink className="tool" href={`/calculators/${t.slug}/`} key={t.slug}><span className="tag">{t.name}</span><h3>{t.tagline}</h3><p>{t.blurb}</p><div className="tool-link">Open calculator →</div></SmartLink>)}</div>
        </div></section>
      )}
      <FinalCta text="Numbers are a starting point. When you want them turned into a plan, start with a conversation. No pressure. No mortgage-speak." />
    </SiteLayout>
  );
}
